import { Alert, Box, FormHelperText, FormLabel, Grid, Stack } from '@mui/material'
import { GeneratorInput } from '../types'
import { FormikErrors, FormikHandlers, FormikHelpers, FormikTouched, getIn } from 'formik'
import { useTranslation } from 'react-i18next'
import GeneratorRelayThresholdField from './GeneratorRelayThresholdField'
import GeneratorRelayThresholdSlider from './GeneratorRelayThresholdSlider'
import { roundValue } from '../utils/string'
import GeneratorAdditionalLoadField from './GeneratorAdditionalLoadField'
import { PowerUnit } from '../enums'
import {
  convertKilovoltAmperesToAmperes,
  convertKilovoltAmperesToKilowatts
} from '../utils/convert'

export interface GeneratorRelayThresholdFieldsProps {
  values: GeneratorInput
  errors: FormikErrors<GeneratorInput>
  touched: FormikTouched<GeneratorInput>
  isSubmitting: boolean
  setFieldValue: FormikHelpers<GeneratorInput>['setFieldValue']
  handleBlur: FormikHandlers['handleBlur']
  handleChange: FormikHandlers['handleChange']
}

export default function GeneratorRelayThresholdFields({
  values,
  errors,
  touched,
  isSubmitting,
  setFieldValue,
  handleBlur,
  handleChange
}: GeneratorRelayThresholdFieldsProps) {
  const { t } = useTranslation('generator')

  const apparantPower = values.apparentPower
  const nominalVoltage = values.nominalVoltage
  const additionalLoadUnit = values.customData?.additionalLoadUnit ?? PowerUnit.KILOWATT
  const additionalLoad = values.customData?.additionalLoad ?? 0
  const additionalLoadThreshold =
    additionalLoadUnit === PowerUnit.KILOWATT
      ? (additionalLoad / convertKilovoltAmperesToKilowatts(apparantPower, 1)) * 100
      : (additionalLoad / convertKilovoltAmperesToAmperes(apparantPower, nominalVoltage)) * 100

  function isError(field: string) {
    const fieldTouched = getIn(touched, field)
    const fieldErrors = getIn(errors, field)

    return Boolean(fieldTouched && fieldErrors)
  }

  function getHelperText(field: string) {
    const fieldTouched = getIn(touched, field)
    const fieldErrors = getIn(errors, field)

    return fieldTouched ? fieldErrors : undefined
  }

  return (
    <Grid container spacing={6}>
      <Grid item xs={12}>
        <GeneratorAdditionalLoadField
          name="customData.additionalLoad"
          value={values.customData?.additionalLoad}
          unit={values.customData?.additionalLoadUnit}
          disabled={isSubmitting}
          error={isError('customData.additionalLoad')}
          helperText={getHelperText('customData.additionalLoad')}
          fullWidth
          onBlur={handleBlur}
          onChange={handleChange}
          onUnitChange={(unit) => {
            setFieldValue('customData.additionalLoadUnit', unit)
          }}
        />

        {!isNaN(additionalLoadThreshold) && (
          <Alert severity="info" variant="outlined" sx={{ mt: 2, border: 0, px: 0, py: 0 }}>
            {t('additionLoadIsOfApparentPower', {
              value: roundValue(additionalLoadThreshold, 1)
            })}
          </Alert>
        )}
      </Grid>

      <Grid item xs={12}>
        <FormLabel>{t('relayControlThresholds')}</FormLabel>
      </Grid>

      <Grid item xs={12} sm={6}>
        <GeneratorRelayThresholdField
          name="customData.relayOnThreshold"
          label={`${t('relayOnThreshold')} (%)`}
          value={values.customData?.relayOnThreshold || undefined}
          error={isError('customData.relayOnThreshold')}
          helperText={getHelperText('customData.relayOnThreshold')}
          disabled={isSubmitting}
          fullWidth
          onBlur={handleBlur}
          onChange={handleChange}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <GeneratorRelayThresholdField
          name="customData.relayOffThreshold"
          label={`${t('relayOffThreshold')} (%)`}
          value={values.customData?.relayOffThreshold || undefined}
          error={isError('customData.relayOffThreshold')}
          helperText={getHelperText('customData.relayOffThreshold')}
          disabled={isSubmitting}
          fullWidth
          onBlur={handleBlur}
          onChange={handleChange}
        />
      </Grid>

      <Grid item xs={12}>
        <Box sx={{ px: 4 }}>
          <GeneratorRelayThresholdSlider
            value={[
              values.customData?.relayOnThreshold ?? 0,
              values.customData?.relayOffThreshold ?? 100
            ]}
            minDistance={additionalLoadThreshold ?? undefined}
            onChange={(_, thresholds) => {
              if (Array.isArray(thresholds) && thresholds.length === 2) {
                setFieldValue('customData.relayOnThreshold', thresholds[0])
                setFieldValue('customData.relayOffThreshold', thresholds[1])
              }
            }}
          />
        </Box>

        <Stack direction="row" justifyContent="space-between" gap={2}>
          {values?.customData?.relayOnThreshold !== undefined && (
            <FormHelperText>
              {t('relayIsTurnedOnWhenPowerDropsBelow', {
                threshold: `${roundValue(values.customData.relayOnThreshold, 2)}%`
              })}
            </FormHelperText>
          )}

          {values?.customData?.relayOffThreshold !== undefined && (
            <FormHelperText>
              {t('relayIsTurnedOffWhenPowerRisesAbove', {
                threshold: `${roundValue(values.customData.relayOffThreshold, 2)}%`
              })}
            </FormHelperText>
          )}
        </Stack>
      </Grid>
    </Grid>
  )
}
